import { useState } from 'react'
import { useRequest } from 'ahooks'

import { useModal } from '$contexts/Modal'

import useSignal from '$actions/useSignal'
import useMFABlock from '$actions/useMFABlock'
import use2FAHelper from '$actions/use2FAHelper'
import useCurrentAuthRTC from '$actions/useCurrentAuthRTC'

import { asyncSuccessResolver, toast } from '$app/utilities'
import { ToastMessage } from '$app/types'

import api from '$model/api'
import { errorMessageResolver, successResolver } from '$services/api/common'
import { PasskeyUpdateFormProps } from '$hook-forms/PasskeyUpdateForm'
import { MFAPasskeysResponse } from '$services/api/types'

export type UseUpdateOptions = {
  onUpdated?: () => void
}

export type UseUpdateReturn = ReturnType<typeof useUpdate>

const updateReqToastMsg: ToastMessage = {
  loading: 'Updating..',
  error: errorMessageResolver,
  success: 'Passkey successfully updated.'
}

type Passkey = MFAPasskeysResponse['data']['passkeys'][number]

export const useUpdate = ({ onUpdated }: UseUpdateOptions) => {
  const [passkey, setPassKey] = useState<Passkey>()

  const modal = useModal({
    disabledOverlayClick: true,
    onClose: () => {
      reset()
      setPassKey(undefined)
      mfaBlock.exit()
    },
    title: 'Update Your Passkey'
  })

  const { mfaAuth: apiInstance } = useSignal(api)
  const authRTC = useCurrentAuthRTC()
  const { mfaUpdatePasskey } = apiInstance

  const updateReq = useRequest(
    asyncSuccessResolver(mfaUpdatePasskey, successResolver),
    {
      manual: true,
      onError: err => {
        if (twoFAHelper.is2FAError(err)) {
          mfaBlock.modalControls.open()
        }
      },
      onSuccess: () => {
        modal.modalControls.close()
        onUpdated?.()
      }
    }
  )

  const isLoading = updateReq.loading

  const twoFAHelper = use2FAHelper({ getMfaBlock: () => mfaBlock })
  const preferred_factor = twoFAHelper.getPreferredFactorByError(
    updateReq.error
  )
  const available_factors = twoFAHelper.getFactorsByError(updateReq.error)

  const mfaBlock = useMFABlock({
    apiInstance,
    authRTC,
    data: { available_factors, preferred_factor },
    onCancelled: modal.modalControls.close,
    onVerified: mfa_token => {
      const { params, runAsync } = updateReq

      // Make sure there was a previous call
      if (!params.length) return

      const [data] = params

      // Auto retry the request with token
      toast
        .promise(runAsync({ ...data, mfa_token }), updateReqToastMsg)
        .unwrap()
        .catch(twoFAHelper.reChallengeOrExitOnError)
    }
  })

  const { mfa_token } = mfaBlock

  const reset = () => {
    updateReq.cancel()
    updateReq.mutate()
  }

  const onUpdate: PasskeyUpdateFormProps['onSuccess'] = (reset, data) => {
    if (!passkey) return

    toast.promise(
      updateReq.runAsync({ ...data, mfa_token, id: passkey.id }).then(reset),
      updateReqToastMsg
    )
  }

  const onUpdateStart = (_passkey: Passkey) => {
    setPassKey(_passkey)
    modal.modalControls.open()
  }

  const onUpdateCancel = () => {
    modal.modalControls.close()
  }

  return {
    modal,
    onUpdate,
    onUpdateStart,
    onUpdateCancel,
    isLoading,
    mfaBlock,
    passkey
  }
}

export default useUpdate
